import * as React from 'react';

/**
 * Copies a value to the clipboard on click, with a copy icon on hover and a tick once copied.
 *
 * @param {object}                  props
 * @param {string | (() => string)} props.value       Text to copy, or a function resolved at click time.
 * @param {string}                  [props.className] Classes for the clickable text.
 * @param {React.ReactNode}         props.children    What the user clicks.
 * @param {boolean}                 [props.block]     Lays the icon out beside block content that wraps.
 * @param {boolean}                 [props.floatingIcon] Draws the icon just past the text without taking room,
 *                                                    for a link in a row that must stay evenly spaced.
 * @param {string}                  [props.label]     What the button copies, read out and shown on hover, for a
 *                                                    button whose content does not say it (an icon by itself).
 */
export const CopyButton = ({ value, className, children, block, floatingIcon, label }) => {
    const [copied, setCopied] = React.useState(false);

    const handleCopy = async (e) => {
      e.preventDefault();
      e.stopPropagation();
      try {
        // A function value resolves at click time: for content that must be
        // read live (the current URL for a section link) rather than at render.
        await navigator.clipboard.writeText(typeof value === "function" ? value() : value);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy text: ", err);
      }
    };

    // Default is inline-flex items-center — vertically centres the icon against
    // whatever the caller wraps (short text, a pill, a badge). For multi-line
    // block content (long URLs that wrap, code blocks) pass `block` so the icon
    // trails inline at the end of the last wrap line instead of reserving a
    // fixed icon column that shrinks the content.
    const layoutClass = block ? 'block' : 'inline-flex items-center';

    // The icon normally keeps its room beside the text even while hidden, so nothing moves when it
    // appears. Floating, it hangs past the end of the text and takes no room at all.
    const iconPlaceClass = floatingIcon ? 'absolute left-full top-1/2 -translate-y-1/2 ml-0.5 w-3 h-3' : 'inline-block ml-1 align-middle w-3.5 h-3.5';

    return (
      <button
        type="button"
        onClick={handleCopy}
        className={`group cursor-pointer align-middle ${layoutClass} ${floatingIcon ? 'relative' : ''} ${className || ''}`}
        title={copied ? "Copied!" : (label ?? "Copy to clipboard")}
        aria-label={label}
      >
        {children}
        {copied ? (
          <svg className={`${iconPlaceClass} text-green-500 dark:text-green-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg className={`${iconPlaceClass} text-foreground-subtle opacity-0 group-hover:opacity-100 transition-opacity duration-200`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        )}
      </button>
    );
};
